import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExpedientes, useMe } from '../lib/hooks';
import { NewsWidget } from '../components/news-widget';
import { RemindersWidget } from '../components/reminders-widget';
import { DemoPlayButton } from '../lib/demo-tour';
import { loadDraft, clearDraft, isMeaningful } from '../lib/draft';
import { Pill, Skeleton, cn, friendlyEstado } from '../components/ui';
import { PersonaIcon } from '../components/icons';
import { UserPlus, ChevronRight, ArrowRight, Clock, X, TriangleAlert, RefreshCw } from 'lucide-react';

const PENDIENTE = new Set(['Nuevo', 'En Revisión', 'Docs Pendientes', 'Actualización Requerida', 'Escalado']);
const LISTO = new Set(['Aprobado', 'Activo']);
const WEEKLY_GOAL = 5;

function getWeekStart(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1)); // back to Monday
  return d;
}

export default function Home() {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const { data: exps, isLoading, isError, refetch } = useExpedientes();
  const [draft, setDraft] = useState(() => {
    const d = loadDraft();
    return isMeaningful(d) ? d : null;
  });

  const list = exps ?? [];
  const total = list.length;
  const listos = list.filter((e) => LISTO.has(e.estado)).length;
  const pendientes = list.filter((e) => PENDIENTE.has(e.estado)).length;
  const recientes = list.slice(0, 4);
  const firstName = me?.fullName.split(' ')[0] ?? '';
  const weekStart = getWeekStart();
  const weeklyNew = list.filter((e) => {
    const d = new Date(e.started);
    return !isNaN(d.getTime()) && d >= weekStart;
  }).length;
  const weekPct = Math.min(1, weeklyNew / WEEKLY_GOAL);

  return (
    <div className="animate-slide-up space-y-8">
        {/* HERO */}
        <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary to-primary-deep p-7 text-white shadow-[0_24px_60px_-24px_rgba(95,31,41,.85)] sm:p-10">
          {/* decorative depth */}
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-20 right-24 h-44 w-44 rounded-full bg-white/5 blur-2xl" />

          <div className="relative grid gap-8 md:grid-cols-[1.5fr_1fr] md:items-center">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/60">
                {new Date().toLocaleDateString('es-PA', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
              <h1 className="mt-2 font-serif text-3xl font-medium leading-tight sm:text-5xl">
                Hola, {firstName}
              </h1>
              <p className="mt-2 max-w-md text-base text-white/75 sm:text-lg">
                Registra y da seguimiento a tus clientes, paso a paso. Empecemos.
              </p>
              <button
                data-tour="home-cta"
                onClick={() => navigate('/expedientes/nuevo')}
                className="group mt-6 inline-flex min-h-[3.5rem] items-center gap-3 rounded-2xl bg-white px-7 text-base font-bold text-primary shadow-lg transition-all hover:shadow-xl active:scale-[.99]"
              >
                <UserPlus className="h-5 w-5" strokeWidth={2.5} />
                Registrar nuevo cliente
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" strokeWidth={2.5} />
              </button>
              <div className="mt-3">
                <DemoPlayButton className="inline-flex items-center gap-2 text-sm font-bold text-white/75 transition-colors hover:text-white" />
              </div>
            </div>

            {/* weekly goal panel */}
            <div className="rounded-2xl border border-white/15 bg-white/10 p-5 backdrop-blur-sm">
              {isLoading ? (
                <div className="space-y-3">
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm font-semibold text-white/80">Tu semana</span>
                    <div className="h-4 w-12 animate-pulse rounded bg-white/20" />
                  </div>
                  <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-white/20" />
                  <div className="h-4 w-48 animate-pulse rounded bg-white/20" />
                </div>
              ) : (
                <>
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm font-semibold text-white/80">Tu semana</span>
                    <span className="font-mono text-sm text-white/70">{Math.min(weeklyNew, WEEKLY_GOAL)} / {WEEKLY_GOAL}</span>
                  </div>
                  <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-white/20">
                    <div
                      className="h-full rounded-full bg-white transition-[width] duration-700 ease-out"
                      style={{ width: `${weekPct * 100}%` }}
                    />
                  </div>
                  <p className="mt-3 text-sm text-white/75">
                    {weeklyNew >= WEEKLY_GOAL
                      ? 'Meta de la semana alcanzada. Excelente trabajo.'
                      : `Te faltan ${WEEKLY_GOAL - weeklyNew} para tu meta semanal.`}
                  </p>
                </>
              )}
            </div>
          </div>
        </section>

        {/* RESUME DRAFT */}
        {draft && (
          <div className="relative">
            <button
              onClick={() => navigate('/expedientes/nuevo')}
              className="flex w-full items-center gap-4 rounded-2xl border-2 border-primary/30 bg-primary/5 p-4 pr-16 text-left transition-colors hover:border-primary/50"
            >
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/15 text-primary-dark">
                <Clock className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold text-ink">Continuar donde quedaste</span>
                <span className="block truncate text-sm text-ink-soft">
                  {draft.name?.trim() || 'Cliente sin nombre'} · paso {draft.idx + 1}
                </span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => { clearDraft(); setDraft(null); }}
              className="absolute right-4 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full text-ink-faint hover:bg-panel hover:text-ink"
              aria-label="Descartar borrador"
            >
              <X className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>
        )}

        {/* STAT RIBBON */}
        {isLoading ? (
          <section className="grid grid-cols-3 gap-3 sm:gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-[90px] rounded-2xl" />
            ))}
          </section>
        ) : isError ? (
          <section className="flex flex-wrap items-center gap-4 rounded-2xl border border-line bg-panel p-4 shadow-card">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-tone-red-bg text-tone-red">
              <TriangleAlert className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <span className="min-w-0 flex-1 text-sm font-semibold text-ink">
              No se pudo cargar tu resumen. Verifica tu conexión.
            </span>
            <button
              onClick={() => void refetch()}
              className="inline-flex items-center gap-2 rounded-lg border border-line-strong bg-panel px-4 py-2.5 text-sm font-semibold text-ink hover:border-ink-faint"
            >
              <RefreshCw className="h-4 w-4" strokeWidth={2} />
              Reintentar
            </button>
          </section>
        ) : (
          <section data-tour="home-stats" className="grid grid-cols-3 gap-3 sm:gap-4">
            <Stat label="Clientes" value={total} onClick={() => navigate('/expedientes')} />
            <Stat label="Listos" value={listos} tone="primary" onClick={() => navigate('/expedientes')} />
            <Stat label="Pendientes" value={pendientes} tone="amber" onClick={() => navigate('/expedientes')} />
          </section>
        )}

        {/* RECENT CLIENTS */}
        <section>
          <div className="mb-4 flex items-end justify-between">
            <h2 className="font-serif text-2xl font-medium text-ink">Clientes recientes</h2>
            <button
              onClick={() => navigate('/expedientes')}
              className="inline-flex items-center gap-1 text-sm font-bold text-primary hover:text-primary-dark"
            >
              Ver todos <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
            </button>
          </div>

          {isLoading ? (
            <div className="grid gap-3 sm:grid-cols-2 [&>*]:min-w-0">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-[72px] rounded-2xl" />
              ))}
            </div>
          ) : isError ? (
            <div className="rounded-3xl border border-line bg-panel px-6 py-12 text-center text-ink-soft shadow-card">
              No se pudieron cargar tus clientes. Verifica tu conexión e inténtalo de nuevo.
            </div>
          ) : recientes.length === 0 ? (
            <div className="rounded-3xl border border-line bg-panel px-6 py-12 text-center text-ink-soft shadow-card">
              Aún no tienes clientes. Registra el primero arriba.
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 [&>*]:min-w-0">
              {recientes.map((r) => {
                const fe = friendlyEstado(r.estado);
                return (
                  <button
                    key={r.id}
                    onClick={() => navigate(`/expedientes/${r.id}`)}
                    className="flex items-center gap-4 rounded-2xl border border-line bg-panel p-4 text-left shadow-card transition-all hover:border-ink-faint active:scale-[.99]"
                  >
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-primary/10">
                      <PersonaIcon persona={r.persona} className="h-6 w-6 text-primary-dark" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-bold text-ink">{r.name}</span>
                      <span className="mt-1 flex flex-wrap items-center gap-2">
                        <Pill tone={fe.tone}>{fe.label}</Pill>
                        {r.pep && <Pill tone="red">PEP</Pill>}
                      </span>
                    </span>
                    <ChevronRight className="h-5 w-5 shrink-0 text-ink-faint" strokeWidth={2} />
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* RECORDATORIOS — its own dedicated, scrollable space */}
        <RemindersWidget />

        {/* NOVEDADES — its own dedicated, scrollable space */}
        <NewsWidget />
    </div>
  );
}

function Stat({
  label,
  value,
  tone = 'default',
  onClick,
}: {
  label: string;
  value: number;
  tone?: 'default' | 'primary' | 'amber';
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="rounded-2xl border border-line bg-panel p-4 text-left shadow-card transition-all hover:border-ink-faint active:scale-[.99] sm:p-5"
    >
      <div
        className={cn(
          'font-serif text-3xl font-medium sm:text-4xl',
          tone === 'primary' ? 'text-primary' : tone === 'amber' ? 'text-tone-amber' : 'text-ink'
        )}
      >
        {value}
      </div>
      <div className="mt-1 truncate text-sm font-semibold text-ink-soft">{label}</div>
    </button>
  );
}
